import React, { useState, useEffect, useContext } from 'react';
import { Icon, Button, ButtonIcon, CardV2, MenuList } from 'pepsico-ds';
import { getSpendAnalyticsCardVisibilityFromEnv } from './chatUtils';
import { useNavigate } from 'react-router-dom';
import { urlConstants } from '../../constants/urlConstants';
import { appConstants } from '../../constants/appconstants';
import { getUserClaims, getAuthHeader } from '../../utils/auth/unified-auth';
import { KMSessionContext } from './KMSessionContext';
import './KnowledgeManagementChat.scss';
import { normalizeSessionHistory } from './chatUtils';
import { KM_SUGGESTED_PROMPTS as SUGGESTED_PROMPTS, PROMPTS_PER_PAGE } from './chatConstants';
import { useDispatch, useSelector } from 'react-redux';
import { FetchUserInfoAction } from '../../redux/action/LandingPageAction';

const { genAITermsUrl, privacyNoticeUrl } = urlConstants;

const KnowledgeManagementChat = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [promptPage, setPromptPage] = useState(0);
  const [slideDirection, setSlideDirection] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showAgentMenu, setShowAgentMenu] = useState(false);

  const sessionContext = useContext(KMSessionContext);
  const dispatch = useDispatch();
  const userAccessData = useSelector((state) => state.getUserAccessReducer);
  const isAIUser = userAccessData?.isAiUser;
  const hasAgentMenuItems = isAIUser || getSpendAnalyticsCardVisibilityFromEnv();
  const [userInfo, setUserInfo] = useState({});
  const [isUserDataFetched, setIsUserDataFetched] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const displaySessionHistory = sessionContext?.sessionHistory?.length > 0 ? sessionContext.sessionHistory: sessionHistory;

  const visiblePrompts = SUGGESTED_PROMPTS.slice(promptPage * PROMPTS_PER_PAGE, (promptPage + 1) * PROMPTS_PER_PAGE);
  const totalPromptPages = Math.ceil(SUGGESTED_PROMPTS.length / PROMPTS_PER_PAGE);

  const handlePromptNav = (direction) => {
    if (isAnimating) {
      return;
    }
    const nextPage = direction === 'left' ? 
      promptPage - 1 
      : promptPage + 1;
    if (nextPage < 0 || nextPage >= totalPromptPages) {
      return;
    }
    setIsAnimating(true);
    setSlideDirection(direction === 'left' ? 'slide-right' : 'slide-left');
    setTimeout(() => {
      setSlideDirection(direction === 'left' ? 'slide-in-from-left' : 'slide-in-from-right');
      setPromptPage(nextPage);
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDirection('');
      }, 300);
    }, 300);
  };

  /* istanbul ignore next */
  useEffect(() => {
    async function fetchUserInfo() {
      const user = await getUserClaims();
      if (user) {
        setUserInfo(user);
        setIsUserDataFetched(true);
        dispatch(FetchUserInfoAction(user));
        localStorage.setItem('gpid', btoa(JSON.stringify(user?.gpid)));
      }
    }
    setIsDataLoading(true);
    fetchUserInfo();
  }, []);

  /* istanbul ignore next */
  useEffect(() => {
    return () => {
      dispatch({
        payload: 'RESET_USER_INFO_ACTION',
        type: 'RESET_USER_INFO_ACTION',
      });
    };
  }, []);

  useEffect(() => {
    if (showSidebar) { fetchSessionHistory(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSidebar]);

  const fetchSessionHistory = async () => {
    setHistoryError('');
    setIsHistoryLoading(true);
    try {
      const gpid = localStorage.getItem('gpid') ? atob(localStorage.getItem('gpid')) : '';
      const { Authorization } = await getAuthHeader();
      const res = await fetch(
        `${appConstants.MI_AI_BASE_URL}/api/v1/chats?chatbot=km`,
        {
          method: 'GET',
          headers: { Authorization, 'Content-Type': 'application/json' },
        },
      );
      if (!res.ok) { throw new Error(`Session history failed: ${res.status}`); }
      const data = await res.json();
      const normalized = normalizeSessionHistory(data);
      setSessionHistory(normalized);
      if (sessionContext?.setSessionHistory) {
        sessionContext.setSessionHistory(normalized);
      }
    } catch (err) {
      setHistoryError('Unable to load sessions.');
      setSessionHistory([]);
      console.error('Session history error:', err);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      navigate('/ai-assistant/knowledge-management-conversations', { state: { message: message.trim() },});
    }
  };

  const handlePromptSend = (prompt) => {
    navigate('/ai-assistant/knowledge-management-conversations', {state: { message: prompt },});
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="mi-chat-container">
      <div className="mi-subheader-2">
        <div className="mi-subheader-left">
          <button className="mi-back-btn" onClick={() => navigate("/ai-assistant")} >
            <Icon icon="arrow_back" style={{ color: '#006FCF' }} />
          </button>
          <button className="mi-sidebar-toggle-btn" onClick={() => setShowSidebar(!showSidebar)} style={{ marginLeft: '6px', color: '#006FCF' }}>
            <Icon icon="view_sidebar" />
          </button>
          <div className="mi-agent-badge"
             style={{
                  marginLeft: '6px',
                  borderRadius: '9999px',
                  borderColor: '#8947E6',
                  background: 'linear-gradient(150deg, #591EAD 13.45%, #AD80E4 85.65%)',
                }}>KM</div>
          <span
            className="mi-agent-name"
            style={{ marginLeft: '8px', cursor: 'pointer' }}
            tabIndex={0}
            role="button"
            onClick={() => setShowAgentMenu(prev => !prev)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowAgentMenu(prev => !prev); }}
          >Knowledge Management</span>
          {hasAgentMenuItems && (
            <Icon icon="arrow_drop_down" style={{ cursor: 'pointer' }} onClick={() => setShowAgentMenu(prev => !prev)} />
          )}
        </div>

        <div className="mi-subheader-right">
        </div>
      </div>

      {showAgentMenu && hasAgentMenuItems && (
      <MenuList
        className="ph-agent-select"
        hideMenuListOnSelection
        customNoResultMessage=""
        emptyStateMessage=""
        height={240}
        isClearable
        onHideMenuList={() => setShowAgentMenu(false)}
        items={[
          ...(isAIUser ? [{
            id: '/ai-assistant/market-intelligence',
            label: 'Market Intelligence',
          }] : []),
          ...(getSpendAnalyticsCardVisibilityFromEnv() ? 
          [{
            label: 'Spend Analytics',
            id: '/spend-analytics-ai-agent',
          }] : []),
        ]}
        selectedItemCallback={function S8e(item)
            { 
              if (item?.[0]?.id) {
                  navigate(item?.[0].id); 
              } 
        }}
        selectedItems={[]}
        showSearch={false}
        width=""
        type="single"
        size="medium"
        style={{}}
        listHeaderLabel=""
      />
      )}

      <div className="mi-chat-body">
        {showSidebar && (
          <div className="mi-chat-sidebar">
            <div className="mi-chats-header">
              <span>
                Chats
              </span>
              <button onClick={() => setShowSidebar(false)} className="mi-close-sidebar" >
                <Icon icon="close" />
              </button>
            </div>
            <button className="mi-new-chat-btn">
              <span className="mi-new-chat-plus">+</span>
              <span>
                New chat
              </span>
            </button>
            <div className="mi-recents">
              <div className="mi-recents-label">
                Recents
              </div>
              
              {!isHistoryLoading &&
                !historyError &&
                displaySessionHistory.length === 0 && (
                  <div className="mi-recent-item">
                    No sessions
                  </div>
                )}
              {!isHistoryLoading && historyError && (
                <div className="mi-recent-item">
                  {historyError}
                </div>
              )}
              {!isHistoryLoading &&
                !historyError &&
                displaySessionHistory.map((chat) => (
                  <div key={chat.sessionId} className="sa-conv-recent-row">
                    <button
                      className="mi-recent-item sa-conv-recent-item"
                      onClick={() =>
                        navigate('/ai-assistant/knowledge-management-conversations', {
                          state: {
                            session_id: chat.sessionId,
                            conversation_id: chat.conversationId,
                          },
                        })
                      }
                      title={chat.title}
                    >
                      <div className="sa-conv-recent-title-wrap" style={{ width: '100%', overflow: 'hidden' }}>
                        <span className="sa-conv-recent-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>
                          {chat.title}
                        </span>
                      </div>
                    </button>
                  </div>
                ))}
              {isHistoryLoading && (<div className="mi-recent-item">Loading sessions...</div>)}  
              
            </div>
          </div>
        )}

        <div className={`mi-chat-main`}>
          <>
              <div className="mi-chat-welcome">
                <div className="mi-welcome-icon" style={{
                  borderRadius: '9999px',
                  borderColor: '#8947E6',
                  background: 'linear-gradient(150deg, #591EAD 13.45%, #AD80E4 85.65%)',
                }}>KM</div>
                <h1 className="mi-welcome-title">Welcome to Knowledge Management. How can I help?</h1>
                <p className="mi-welcome-subtitle">
                  <span style={{ fontWeight: '700', textTransform: 'uppercase' }}>Sources covered</span>: Global PepsiCo Procurement Policy and Procurement Productivity Framework
                </p>
              </div>

              <div className="mi-chat-input-area">
                <div className="mi-input-wrapper">
                  <input
                    type="text"
                    className="mi-input"
                    placeholder="Send a message to Knowledge Management AI Assistant."
                    onChange={(e) => setMessage(e.target.value)}
                    value={message}
                    onKeyPress={handleKeyPress}
                  />
                  <Button
                    className="mi-send-btn"
                    disabled={!message.trim()}
                    onClick={handleSendMessage}
                    iconTrailing="send"
                    variant="primary"
                    size="medium"
                  />
                </div>
                <p className="mi-input-disclaimer">
                  By messaging the AI assistant, you confirm that you have read and agree to our{' '}
                  <a href={genAITermsUrl} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href={privacyNoticeUrl} target="_blank" rel="noopener noreferrer">Privacy Notice</a>.
                  Please do not share sensitive information. Chats may be reviewed and used to improve our models. AI can make mistakes, so please verify important information.
                </p>
                <div className="mi-suggested-prompts">
                  <ButtonIcon
                    onClick={() => handlePromptNav('left')}
                    disabled={promptPage === 0 || isAnimating}
                    type="default"
                    style={{ opacity: promptPage === 0 ? 0.4 : 1 }}
                    size="medium"
                    label={""}
                    color="Interaction/Active"
                    className="mi-prompt-nav"
                    icon={"chevron_left"}
                  />
                  <div className={`mi-prompt-cards-wrapper ${slideDirection}`}>
                    {visiblePrompts.map((prompt, idx) => (
                      <CardV2
                        key={promptPage * PROMPTS_PER_PAGE + idx}
                        className="mi-prompt-card"
                        cardBody={{
                          type: "slot",
                          slot: (
                            <div
                              style={{ cursor: 'pointer' }}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePromptSend(prompt); }}
                              onClick={() => handlePromptSend(prompt)}
                              tabIndex={0}
                              role="button"
                            >
                              {prompt}
                            </div>
                          )
                        }}
                        type="basic"
                        state="interactive"
                        onClick={() => handlePromptSend(prompt)}
                      />
                    ))}
                  </div>
                  <ButtonIcon
                    disabled={promptPage >= totalPromptPages - 1 || isAnimating}
                    type="default"
                    style={{ opacity: promptPage >= totalPromptPages - 1 ? 0.4 : 1 }}
                    onClick={() => handlePromptNav('right')}
                    size="medium"
                    label={""}
                    icon={"chevron_right"}
                    className="mi-prompt-nav"
                    color="Interaction/Active"
                  />
                </div>
              </div>
            </>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeManagementChat;
